import { apiClient } from './client'
import type {
	DataCubeBatch,
	DataCubeDepartmentSummary,
	DataCubeImportResult,
	DataCubeListParams,
	DataCubeRecordListResult,
	DataCubeUserSummary,
	DataCubeUserWeight,
} from '@/types/data-cube'

const hasValue = (value: unknown) => value !== undefined && value !== null && value !== ''

const buildSearch = (params: DataCubeListParams = {}) => {
	const search = new URLSearchParams()
	if (hasValue(params.page)) search.set('page', String(params.page))
	if (hasValue(params.page_size)) search.set('page_size', String(params.page_size))
	if (hasValue(params.sort)) search.set('sort', String(params.sort))
	if (hasValue(params.order)) search.set('order', String(params.order))
	if (hasValue(params.batch_id)) search.set('batch_id', String(params.batch_id))
	if (hasValue(params.scope)) search.set('scope', String(params.scope))
	if (hasValue(params.department_id)) search.set('department_id', String(params.department_id))
	if (hasValue(params.start_date)) search.set('start_date', String(params.start_date))
	if (hasValue(params.end_date)) search.set('end_date', String(params.end_date))
	return search.toString()
}

const withSearch = (endpoint: string, params: DataCubeListParams) => {
	const search = buildSearch(params)
	return search ? `${endpoint}?${search}` : endpoint
}

const buildUserWeightSearch = (params: DataCubeListParams = {}) => {
	const search = new URLSearchParams()
	if (hasValue(params.scope)) search.set('scope', String(params.scope))
	if (hasValue(params.department_id)) search.set('department_id', String(params.department_id))
	if (hasValue(params.start_date)) search.set('start_date', String(params.start_date))
	if (hasValue(params.end_date)) search.set('end_date', String(params.end_date))
	return search.toString()
}

const withUserWeightSearch = (endpoint: string, params: DataCubeListParams) => {
	const search = buildUserWeightSearch(params)
	return search ? `${endpoint}?${search}` : endpoint
}

export const dataCubeApi = {
	records: async (params: DataCubeListParams) => {
		const response = await apiClient.get<DataCubeRecordListResult>(withSearch('/data-cube/records', params))
		return response.data
	},
	batches: async () => {
		const response = await apiClient.get<DataCubeBatch[]>('/data-cube/batches')
		return response.data
	},
	template: async () => {
		await apiClient.download('/data-cube/template', undefined, { method: 'GET' })
	},
	import: async (file: File) => {
		const formData = new FormData()
		formData.append('excel', file)
		const response = await apiClient.upload<DataCubeImportResult>('/data-cube/import', formData)
		return response.data
	},
	deleteRecord: async (id: number) => {
		await apiClient.delete(`/data-cube/records/${id}`)
	},
	batchDeleteRecords: async (selected: number[]) => {
		await apiClient.post('/data-cube/records/batch-delete', { selected })
	},
	deleteBatch: async (id: number) => {
		await apiClient.delete(`/data-cube/batches/${id}`)
	},
	userSummary: async (params: DataCubeListParams) => {
		const response = await apiClient.get<DataCubeUserSummary[]>(withSearch('/data-cube/user-summary', params))
		return response.data
	},
	userWeight: async (params: DataCubeListParams) => {
		const response = await apiClient.get<DataCubeUserWeight[]>(withUserWeightSearch('/data-cube/user-weight', params))
		return response.data
	},
	departmentSummary: async (params: DataCubeListParams) => {
		const response = await apiClient.get<DataCubeDepartmentSummary[]>(withSearch('/data-cube/department-summary', params))
		return response.data
	},
}
